import { useCallback, useContext, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Button } from '@Components/Button/Button';
import { Checkbox } from '@Components/FormCheck';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Placement as PlacementConst } from '@Components/designConstants';
import { Neutral600 } from '@src/utils/colors';
import { useCreativeMap } from '../../redux/creative';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { StaticCard } from './Cards';
import { PieSetter } from './PieSetter';
import { NonCartContext } from './noncart-context';
import { ViewPlacementMetadata } from './ViewPlacementMetadata';
import { StreamingCreativesContext, TabKey } from './streaming-creatives-constants';

interface RotationCardData {
  isci: string;
  name: string;
  key: string;
  creative: string;
  length: number;
  value: number;
  color: string;
  isLive: boolean;
  creativeClickthroughUrl: string | null;
  audioCompanion: string | null;
}

export const ViewPlacement = () => {
  const {
    shopData,
    date,
    workspaceOptions,
    setWorkspaceMap,
    selectedMap,
    goToTab,
    setSelectedMap
  } = useContext(StreamingCreativesContext);

  const { viewPlacementId, setViewPlacementId, filteredPlacements } = useContext(NonCartContext);

  const { cid: company } = useCompanyInfo();
  const { creativeMap, colorMap, isciLiveOnDate } = useCreativeMap({
    company,
    mediaTypes: ['streaming', 'audio', 'display']
  });

  const [autoSelectSelf, setAutoSelectSelf] = useState(true);
  const [selectSameCheck, setSelectSameCheck] = useState(false);

  const placement = useMemo(
    () => (viewPlacementId ? shopData.placementMap[viewPlacementId] : undefined),
    [shopData, viewPlacementId]
  );

  const loadRotation = useCallback(() => {
    if (!placement) {
      return;
    }
    const { creatives, rotationKey, id } = placement;

    const localCreativeMap: Record<string, number> = {};
    for (const { isci, pct, start_date, end_date } of creatives) {
      if (start_date <= date && (!end_date || end_date >= date)) {
        localCreativeMap[isci] = pct;
      }
    }
    const newWorkspace: Record<string, number> = {};
    for (const { isci } of workspaceOptions) {
      newWorkspace[isci] = localCreativeMap[isci] || 0;
    }

    setWorkspaceMap(newWorkspace);

    if (selectSameCheck || autoSelectSelf) {
      const newSelectedMap = { ...selectedMap };
      if (autoSelectSelf) {
        newSelectedMap[id] = true;
      }
      if (selectSameCheck) {
        for (const filteredId of filteredPlacements) {
          if (rotationKey === shopData.placementMap[filteredId]?.rotationKey) {
            newSelectedMap[filteredId] = true;
          }
        }
      }
      setSelectedMap(newSelectedMap);
    }

    setViewPlacementId();
    goToTab(TabKey.WORKSPACE);
  }, [
    placement,
    date,
    workspaceOptions,
    autoSelectSelf,
    selectSameCheck,
    selectedMap,
    setWorkspaceMap,
    setSelectedMap,
    goToTab,
    setViewPlacementId,
    filteredPlacements,
    shopData
  ]);

  const [data, placementLevelClickthroughUrl, shouldShowCreativeMetadata] = useMemo<
    [RotationCardData[], string | undefined, boolean]
  >(() => {
    const liveData: RotationCardData[] = [];
    const notLive: RotationCardData[] = [];
    let placementClickthroughUrl: string | undefined;
    let showCreativeMetadata = true;
    if (placement) {
      // If all creatives are using the same clickthrough URL, display it as a placement level
      // clickthrough. If they differ at all, break out the display by ISCI.
      const creativeLevelClickthroughs = placement.creatives.map(
        creative => creative.creative_clickthrough_url || placement.placement_clickthrough_url
      );
      const allUseSameClickthrough = new Set(creativeLevelClickthroughs).size === 1;

      if (allUseSameClickthrough) {
        if (
          creativeLevelClickthroughs.length &&
          creativeLevelClickthroughs[0] &&
          creativeLevelClickthroughs[0] !== placement.placement_clickthrough_url
        ) {
          placementClickthroughUrl = creativeLevelClickthroughs[0] || undefined;
        } else {
          placementClickthroughUrl = placement.placement_clickthrough_url || 'None';
        }
      }

      // Don't render the creative metadata section if we're showing a placement-level
      // clickthrough URL and there are no audio companions to display.
      const hasAudioCompanions = placement.creatives.some(creative => creative.audio_companion);
      if (allUseSameClickthrough && !hasAudioCompanions) {
        showCreativeMetadata = false;
      }

      for (const {
        isci,
        pct,
        start_date,
        end_date,
        creative_clickthrough_url,
        audio_companion
      } of placement.creatives) {
        if (start_date > date || (end_date && end_date < date)) {
          continue;
        }
        const creativeData = creativeMap?.[isci];
        const item: RotationCardData = {
          isci,
          name: isci,
          key: isci,
          creative: creativeData?.name || isci,
          length: creativeData?.length ?? 0,
          value: pct,
          color: colorMap[isci] || 'white',
          isLive: true,
          creativeClickthroughUrl: allUseSameClickthrough
            ? null
            : creative_clickthrough_url || placement.placement_clickthrough_url || 'None',
          audioCompanion: audio_companion ?? null
        };

        if (isciLiveOnDate({ isci, date })) {
          liveData.push(item);
        } else {
          notLive.push({ ...item, color: Neutral600, isLive: false });
        }
      }
    }
    const byIsci = (a: RotationCardData, b: RotationCardData) => a.isci.localeCompare(b.isci);
    return [
      [...liveData.sort(byIsci), ...notLive.sort(byIsci)],
      placementClickthroughUrl,
      showCreativeMetadata
    ];
  }, [placement, date, creativeMap, colorMap, isciLiveOnDate]);

  if (!placement) {
    return null;
  }

  return (
    <Modal show dialogClassName='view-placement-modal' onHide={() => setViewPlacementId()}>
      <Modal.Header closeButton>
        <Modal.Title>
          {placement.network} {placement.platform}{' '}
          <span className='tag-name'>&quot;{placement.name}&quot;</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='rotation-body'>
          {data.length ? (
            <PieSetter data={data} renderItem={elem => <StaticCard {...elem} />} />
          ) : (
            <div className='no-rotation'>No rotation</div>
          )}
        </div>
        <div className='metadata'>
          <ViewPlacementMetadata
            placementLevelClickthroughUrl={placementLevelClickthroughUrl}
            trackingPixels={placement.tracking_pixels}
            creatives={data}
            shouldShowCreativeMetadata={shouldShowCreativeMetadata}
          />
        </div>
      </Modal.Body>
      <Modal.Footer className='modal-footer'>
        <div className='view-placement-footer'>
          <div className='load-rotation'>
            <Button onClick={loadRotation}>Load Into Workspace</Button>
            <Checkbox
              className='check-all'
              id='autoSelectSelf'
              checked={autoSelectSelf}
              onChange={() => setAutoSelectSelf(!autoSelectSelf)}
              label={
                <span>
                  Select this tag as well.&nbsp;
                  <InfoTooltip placement={PlacementConst.TOP}>
                    When this is checked and you load this rotation into the workspace, it will also
                    add this tag to your workspace as if you hit the + in the menu.
                  </InfoTooltip>
                </span>
              }
            />
            <Checkbox
              className='check-all'
              id='selectSameCheck'
              checked={selectSameCheck}
              onChange={() => setSelectSameCheck(!selectSameCheck)}
              label={
                <span>
                  Select all filtered tags with the same rotation.&nbsp;
                  <InfoTooltip placement={PlacementConst.TOP}>
                    If you check this box before you load this rotation into the workspace, it will
                    automatically select every tag with the same rotation. However, it will only
                    select tags you currently have in view. In other words, if you&apos;re currently
                    filtering for a certain network, it will only select tags for that network. For
                    instance, if you&apos;re filtering for &quot;Network is like NBC&quot; and this
                    is a 15s tag, it will select all NBC tags that have the same 15s rotation, but
                    not tags for 30s or from other networks.
                  </InfoTooltip>
                </span>
              }
            />
          </div>
          <Button onClick={() => setViewPlacementId()}>Done</Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
